import { useEffect, Ref } from 'react';
import { AxiosError } from 'axios';
import { GenericPageShimmer } from '../shimmer/GenericPageShimmer/GenericPageShimmer';
import { EntityFormFactory } from './factories/entityFormFactory';
import { GenericApiResponse } from '../shared/interfaces/generics';
import { AsyncValidationForm } from '../shared/components/AsyncValidationForm/AsyncValidationForm';
import { useQuery } from '../request/hooks/useQuery';
import { EntityUIState } from '../request/interfaces/entity-ui-state';
import { ENTITY_FORM_DATA_IDS } from './entityFormDataIds';
import { RetryApiAlert } from '../shared/components/RetryApiAlert/RetryApiAlert';
import { getErrorMessageFromAxiosError } from '../request/utils';
import { AsyncTaskStatusResponse, AsyncTaskResponse } from '../shared/interfaces/async-task';
import { AsyncEntityForm } from './AsyncEntityForm';
import { TemplateRawForm } from '../template-form/interfaces/template-raw-form';
import { TemplateFormState } from '../template-form/interfaces/template-form';
import { EntityFormReducerState } from './reducer/interfaces';
import styles from './EntityForm.module.scss';
import { EntityFormMode, EntityFormType } from './interfaces/entity-form';

export interface EntityFormImperativeRef {
  submitForm: VoidFunction;
}

export interface EntityFormProps {
  onComplete: (data: object) => void;
  entity: EntityFormType;
  isSBSDocsAllowed?: boolean;
  topAdornment?: React.ReactNode;
  formDataResolver: () => Promise<GenericApiResponse<TemplateRawForm>>;
  formMetaOptionalValueResolvers?: {
    [key: string]: {
      resolver: (() => Promise<GenericApiResponse<unknown>>) | null | undefined;
      defaultValue: string | number | Array<unknown> | { [key: string]: unknown };
    };
  };
  formMetaRequiredValueResolvers?: {
    [key: string]: (() => Promise<GenericApiResponse<unknown>>) | null | undefined;
  };
  formSubmitResolver: (values: TemplateFormState) => Promise<GenericApiResponse<object>>;
  testConnectionResolver: (taskId: string) => Promise<GenericApiResponse<AsyncTaskStatusResponse>>;
  testConnectionTaskIdResolver: (values: unknown) => Promise<GenericApiResponse<AsyncTaskResponse>>;
  formImperativeRef: Ref<EntityFormImperativeRef>;
  onStateChange: (state: EntityFormReducerState & { isValidating: boolean }) => void;
  mode: EntityFormMode;
}

const dataIds = ENTITY_FORM_DATA_IDS;

export function EntityForm({
  formDataResolver,
  formMetaRequiredValueResolvers = {},
  formMetaOptionalValueResolvers = {},
  entity,
  mode,
  ...rest
}: EntityFormProps) {
  const {
    data: form,
    uiState,
    error,
    fetch: fetchTemplate
  } = useQuery({
    dataSource$: async () => {
      const promises: Promise<GenericApiResponse<unknown>>[] = [formDataResolver()];
      let metaDataResponse: { [key: string]: unknown } = {};
      Object.keys(formMetaRequiredValueResolvers).forEach(key => {
        const resolver = formMetaRequiredValueResolvers[key];
        if (typeof resolver === 'function') {
          promises.push(resolver());
          metaDataResponse = { ...metaDataResponse, [key]: null };
        }
      });

      // resolves optional values
      let metaOptionalDataResponse: { [key: string]: unknown } = {};
      const optionalResolverPromises: Promise<GenericApiResponse<unknown>>[] = [];
      Object.keys(formMetaOptionalValueResolvers).forEach(key => {
        const { resolver, defaultValue } = formMetaOptionalValueResolvers[key];
        if (typeof resolver === 'function') {
          optionalResolverPromises.push(resolver());
          metaOptionalDataResponse = { ...metaOptionalDataResponse, [key]: defaultValue };
        }
      });

      const response: PromiseSettledResult<GenericApiResponse<unknown>>[] =
        await Promise.allSettled(optionalResolverPromises);
      Object.keys(metaOptionalDataResponse).forEach((key, index) => {
        if (response[index].status === 'fulfilled' && 'value' in response[index]) {
          const { value } = response[index] as PromiseFulfilledResult<GenericApiResponse<unknown>>;
          metaOptionalDataResponse[key] = value.data;
        }
      });

      return Promise.all(promises).then(([formData, ...metaResponse]) => {
        Object.keys(metaDataResponse).forEach((key, index) => {
          metaDataResponse[key] = metaResponse[index].data;
        });

        return EntityFormFactory(formData.data as TemplateRawForm, entity, mode, {
          ...metaDataResponse,
          ...metaOptionalDataResponse
        });
      });
    }
  });

  useEffect(() => {
    fetchTemplate();
  }, []);

  return (
    <div data-id={dataIds.container} className={styles.entityFormContainer}>
      {uiState === EntityUIState.LOADING ? (
        <GenericPageShimmer className='w-100' dataId={dataIds.loadingShimmer} />
      ) : null}

      {uiState === EntityUIState.ERRORED ? (
        <div className='w-100 h-100 center-flex-row justify-center'>
          <RetryApiAlert
            title='Failed to load the form template'
            subTitle={
              getErrorMessageFromAxiosError(error as AxiosError) || 'Retry loading the form'
            }
            secondaryCTAProps={{ onClick: fetchTemplate }}
            dataId={dataIds.retryApiAlert}
          />
        </div>
      ) : null}

      {uiState === EntityUIState.IDLE && !!form ? (
        <AsyncValidationForm>
          <AsyncEntityForm {...rest} entity={entity} form={form} mode={mode} />
        </AsyncValidationForm>
      ) : null}
    </div>
  );
}
